import { createBrowserRouter, Navigate } from 'react-router-dom'
import { lazy, Suspense, type ComponentType } from 'react'
import { getActiveWorldConfig } from '@gamecodex/world-config'
import { RootLayout } from '@/components/layout/RootLayout'
import { LocaleProvider } from '@/i18n'
import { Loading } from '@/components/ui/Loading'
import { NotFoundPage } from '@/features/system/NotFoundPage'
import { RouteErrorBoundary } from '@/features/system/RouteErrorBoundary'
import { isChunkLoadError, reloadOnceForStaleChunk } from '@/lib/staleChunk'

/**
 * Wrap a lazily-loaded page in a themed Suspense fallback.
 *
 * A tab left open across a deploy still holds the OLD chunk manifest; its next
 * client-side navigation `import()`s a hashed file that no longer exists and
 * the SPA fallback answers with index.html — the browser throws
 * "Failed to fetch dynamically imported module". Recover by reloading once so
 * the fresh index.html brings the fresh chunk map; a second failure for the
 * same page is a real error and propagates to the route boundary.
 */
function page(loader: () => Promise<{ default: ComponentType }>) {
  const C = lazy(() => loader().catch((err: unknown) => {
    if (!isChunkLoadError(err) || !reloadOnceForStaleChunk()) throw err
    return new Promise<never>(() => {}) // reloading — never resolve
  }))
  return (
    <Suspense fallback={<Loading label="Parting the veil…" />}>
      <C />
    </Suspense>
  )
}

/** Bare `/` → the active world's default locale (`/en`). */
function RootLocaleRedirect() {
  return <Navigate to={`/${getActiveWorldConfig().locales.default}`} replace />
}

export const routes = [
  // Every page lives under a `/:locale` prefix; the bare root redirects to the
  // world's default locale. `LocaleProvider` validates the segment (and reroutes
  // unprefixed legacy URLs like `/encyclopedia` → `/en/encyclopedia`).
  { path: '/', element: <RootLocaleRedirect /> },
  {
    path: '/:locale',
    element: (
      <LocaleProvider>
        <RootLayout />
      </LocaleProvider>
    ),
    // Distinguishes a genuine 404 (→ NotFoundPage) from an unexpected render
    // crash (→ themed, recoverable error screen). A failed data fetch no longer
    // dead-ends on the 404 page — sections degrade inline instead.
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: page(() => import('@/features/home/HomePage')) },

      // Funnel — fair-trade lead magnets (goal #1)
      { path: 'free', element: page(() => import('@/features/marketing/FreeMagnetPage')) },
      // Play-Now demo — the guided globe → VTT-lobby → character-creation tour.
      { path: 'demo', element: page(() => import('@/features/demo/DemoPage')) },

      // Encyclopedia
      { path: 'encyclopedia', element: page(() => import('@/features/encyclopedia/EncyclopediaPage')) },
      { path: 'encyclopedia/:type/:id', element: page(() => import('@/features/encyclopedia/EntityDetailPage')) },

      // The World / Maps
      { path: 'world', element: page(() => import('@/features/world/WorldPage')) },
      // Eternal City: the living-city simulation opened from a globe hex (worlds with `eternalCity` configured).
      // City map editor: paint a city map floor by floor and check it with the city engine's own reader
      { path: 'world/cities/editor', element: page(() => import('@/features/city/editor/MapEditorPage')) },
      { path: 'world/cities/:slug', element: page(() => import('@/features/city/CityPage')) },
      // Hex-tile-kit map builder (any world with a `mapBuilder.kitId` configured;
      // others get an honest "not available" state — see HexBuilderPage).
      { path: 'map-builder', element: page(() => import('@/features/world/HexBuilderPage')) },

      // Play / VTT
      { path: 'play', element: page(() => import('@/features/play/PlayLobbyPage')) },
      { path: 'play/campaigns/:id', element: page(() => import('@/features/play/CampaignPage')) },
      // F15 — public join-by-code landing (scan a QR / type a code on stream).
      { path: 'play/join/:code', element: page(() => import('@/features/play/JoinPage')) },
      // Real-table seating page (authenticated users)
      { path: 'play/tables/:id', element: page(() => import('@/features/play/RealTablePage')) },

      // Commerce (play-commerce surface) — checkout/marketplace/perks/host tools
      // G2 ticketed-table checkout
      { path: 'play/tables/:id/ticket', element: page(() => import('@/features/commerce/TicketCheckoutPage')) },
      // G4 host management of tip goals + bounties (overlay render is separate)
      { path: 'play/tables/:id/tip-goals', element: page(() => import('@/features/commerce/TipGoalManagerPage')) },
      // G5 marketplace browse/install
      { path: 'marketplace', element: page(() => import('@/features/commerce/MarketplacePage')) },
      // G3 subscriber perks funnel
      { path: 'subscribe', element: page(() => import('@/features/commerce/SubscriberPerksPage')) },
      // DTRPG "claim your library" import
      { path: 'claim-library', element: page(() => import('@/features/commerce/DtrpgClaimPage')) },

      // Books & Rules / Store
      { path: 'books', element: page(() => import('@/features/books/BookStorePage')) },
      { path: 'books/:id', element: page(() => import('@/features/books/BookDetailPage')) },
      { path: 'campaigns/:slug', element: page(() => import('@/features/books/CampaignLandingPage')) },
      { path: 'rules', element: page(() => import('@/features/books/RulesPage')) },

      // Backer magic-link landing (Atlas SSO) — exchanges ?token for a session
      { path: 'backer/verify', element: page(() => import('@/features/books/BackerVerifyPage')) },

      // Community
      { path: 'community', element: page(() => import('@/features/community/CommunityPage')) },
      { path: 'community/forums/:category', element: page(() => import('@/features/community/ForumCategoryPage')) },
      { path: 'community/thread/:id', element: page(() => import('@/features/community/ThreadPage')) },
      { path: 'community/legends', element: page(() => import('@/features/community/HallOfLegendsPage')) },

      // My Grymwyrd (dashboard) — protected
      { path: 'me', element: page(() => import('@/features/dashboard/DashboardPage')) },
      { path: 'me/avatar', element: page(() => import('@/features/dashboard/AvatarEditorPage')) },
      { path: 'me/coins', element: page(() => import('@/features/dashboard/CoinHistoryPage')) },
      { path: 'me/contributions', element: page(() => import('@/features/dashboard/ContributionsPage')) },

      // Admin / moderation — protected (admin role)
      { path: 'admin/moderation', element: page(() => import('@/features/admin/ModerationQueuePage')) },
      { path: 'admin/legal', element: page(() => import('@/features/admin/LegalEditorPage')) },
      { path: 'admin/coming-soon', element: page(() => import('@/features/admin/ComingSoonPage')) },
      // Cascade Debugger: read-only view of the style and taxonomy prompt cascades (admin role)
      { path: 'admin/cascade-debugger', element: page(() => import('@/features/city/debug/CascadeDebuggerPage')) },
      // Shardmasters: the god account assigns people to RPGPublisher shards
      { path: 'admin/shardmasters', element: page(() => import('@/features/admin/ShardmastersPage')) },

      // Auth
      { path: 'login', element: page(() => import('@/features/auth/LoginPage')) },
      { path: 'register', element: page(() => import('@/features/auth/RegisterPage')) },
      { path: 'reset-password', element: page(() => import('@/features/auth/ResetPasswordPage')) },
      { path: 'account/password', element: page(() => import('@/features/auth/SetPasswordPage')) },
      // GameCodex Accounts magic-link landing — exchanges ?token for a session.
      { path: 'auth/verify', element: page(() => import('@/features/auth/AccountVerifyPage')) },

      // Legal
      { path: 'legal/:doc', element: page(() => import('@/features/system/LegalPage')) },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
  // Broadcast overlay (feature 8) — a bare OBS browser-source, intentionally
  // OUTSIDE RootLayout (no nav/footer/chrome; transparent background).
  { path: '/overlay/:tableId', element: page(() => import('@/features/overlay/OverlayPage')) },
]

export const router = createBrowserRouter(routes)
